import { Controller } from "@hotwired/stimulus"

// Drives the Record Placement form (new and edit). Searchable curator and
// release selects, per-curator playlists, dynamic split lines, allocation math,
// concurrency badges, per-line entry status, not-in-catalog and over-budget
// soft warnings, client-side submit guards, and full repopulation on a
// server-rejected submit (prefill) so typed input is never lost.
export default class extends Controller {
  static targets = [
    "modeSelect",
    "curatorSearch", "curatorId", "curatorList", "catalogNote", "publicityNote",
    "typeSelect", "totalAmount",
    "linesContainer", "addLineRow", "splitToggle",
    "allocation", "budgetWarning", "playlistWarning", "formMessage", "submit",
    "packageToggle", "packageFields", "packageMonths", "packageNote"
  ]

  static values = {
    curators: Array,
    releases: Array,
    clientBudgets: Object,
    prefill: Object,
    playlistsUrl: String,
    resolveUrl: String,
    editingCommitted: Object
  }

  connect() {
    this.playlists = []
    this.boundClose = this.closeDropdowns.bind(this)
    document.addEventListener("click", this.boundClose)

    // Capture a pristine line template up front so addLine never clones a
    // mutated or hidden line (the source of the toggle-cycle corruption).
    const firstLine = this.element.querySelector(".payment-line")
    this.lineTemplate = firstLine ? this.cleanTemplate(firstLine.cloneNode(true)) : null

    this.prefillForm()
    // applyMode sets pitched/accepted visibility and runs the accepted-state
    // recomputes only when accepted, so a pitch never shows money/playlist UI.
    this.applyMode()
  }

  // ----- Pitched / Accepted mode -----

  isPitched() {
    return this.hasModeSelectTarget && this.modeSelectTarget.value === "pitched"
  }

  modeChanged() {
    this.applyMode()
    this.renderPublicityNote()
    // Switching to Accepted carries the pitch's expected cost into the
    // (blank) total, so the amount never silently resets to 0.00 in the
    // second window (register 2026-08-05, item 5). Blank-only: a typed
    // total is never overwritten.
    if (!this.isPitched()) this.copyExpectedCostIntoTotal()
  }

  copyExpectedCostIntoTotal() {
    if (!this.hasTotalAmountTarget || this.totalAmountTarget.value) return
    const expected = this.element.querySelector('input[name="payment[expected_cost]"]')
    if (expected && expected.value) {
      this.totalAmountTarget.value = expected.value
      this.totalChanged()
    }
  }

  applyMode() {
    const pitched = this.isPitched()
    this.element.querySelectorAll(".pitched-only").forEach(el => { el.style.display = pitched ? "" : "none" })
    this.element.querySelectorAll(".accepted-only, .line-accepted-only").forEach(el => { el.style.display = pitched ? "none" : "" })

    if (pitched) {
      // No money, no playlist: the budget/allocation/playlist warnings do not
      // apply to a pitch. The not-in-catalog warning DOES apply in both modes.
      if (this.hasBudgetWarningTarget) this.budgetWarningTarget.style.display = "none"
      if (this.hasPlaylistWarningTarget) this.playlistWarningTarget.style.display = "none"
      if (this.hasAllocationTarget) this.allocationTarget.style.display = "none"
    } else {
      this.applyTypeLock()
      this.refreshAllocation()
      this.recomputeBudgetWarning()
      this.recomputePlaylistWarning()
    }
  }

  cleanTemplate(node) {
    node.querySelectorAll("input").forEach(i => { if (i.type === "checkbox") i.checked = false; else i.value = "" })
    node.querySelectorAll("[data-line-hidden], [name$='[id]'], .line-destroy").forEach(el => el.remove())
    node.querySelectorAll("select").forEach(s => { s.selectedIndex = 0 })
    node.querySelectorAll(".line-new-release, .line-new-playlist, .line-placed-field, .line-expired-extra, .line-concurrency, .line-release-list, .line-url-resolve, .line-identity, .line-renewal-notice")
      .forEach(p => { p.style.display = "none"; if (p.classList.contains("line-concurrency") || p.classList.contains("line-url-resolve")) p.innerHTML = "" })
    node.querySelectorAll(".line-identity-text").forEach(el => { el.textContent = "" })
    node.querySelectorAll(".line-amount").forEach(i => i.classList.remove("imbalanced"))
    node.style.display = ""
    return node
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose)
  }

  // ----- Prefill (repopulate after server rejection or for edit) -----

  prefillForm() {
    const data = this.prefillValue
    if (!data || !data.lines || data.lines.length === 0) return

    if (data.curator_id) {
      if (this.hasCuratorIdTarget) this.curatorIdTarget.value = data.curator_id
      if (this.hasCuratorSearchTarget && data.curator_name) this.curatorSearchTarget.value = data.curator_name
      const known = (this.curatorsValue || []).find(c => String(c.id) === String(data.curator_id))
      this.renderCuratorNote(known || { current_catalog: data.curator_current_catalog })
    }

    const first = this.element.querySelector(".payment-line")
    this.fillLine(first, data.lines[0])
    for (let i = 1; i < data.lines.length; i++) {
      this.addLine()
      const lines = this.element.querySelectorAll(".payment-line")
      this.fillLine(lines[lines.length - 1], data.lines[i])
    }

    if (data.split && this.hasSplitToggleTarget) {
      this.splitToggleTarget.checked = true
      if (this.hasAddLineRowTarget) this.addLineRowTarget.style.display = "block"
    }

    if (data.curator_id) {
      this.loadPlaylists(data.curator_id, () => this.restorePlaylistSelections())
    }
    this.reindex()
  }

  fillLine(line, data) {
    if (!line || !data) return

    if (data.id) {
      this.ensureHidden(line, "id", data.id)
      this.ensureHidden(line, "_destroy", data.destroy ? "1" : "0", "line-destroy")
      // The playlist this saved line is on in the database (not what a
      // refused submit chose): the "add it above" box stays away only
      // while the line keeps it (addsNewSong).
      line.dataset.savedPlaylist = data.saved_curator_playlist_id || ""
    }

    this.setVal(line, ".line-release-id", data.release_id)
    this.setVal(line, ".line-release-search", data.release_label)

    // Per-line identity (client + song): drives the "Placement for" label and the
    // budget warning. Present even when the line has no SharedRelease (imports).
    this.setVal(line, ".line-client-id", data.shared_client_id)
    this.setVal(line, ".line-song-name", data.song_name)
    // Sibyl's Count it: the PR press clip this line counts.
    this.setVal(line, ".line-press-clip-id", data.dpk_press_clip_id)
    const identityText = [data.client_name, data.song_name].filter(Boolean).join(": ")
    const identityWrap = line.querySelector(".line-identity")
    if (identityText && identityWrap) {
      const textEl = line.querySelector(".line-identity-text")
      if (textEl) textEl.textContent = identityText
      identityWrap.style.display = "block"
    }
    if (data.new_release && Object.values(data.new_release).some(v => v)) {
      const panel = line.querySelector(".line-new-release")
      if (panel) panel.style.display = "block"
      this.setVal(line, ".line-nr-client", data.new_release.client_id)
      this.setVal(line, ".line-nr-song", data.new_release.song_name)
      this.setVal(line, ".line-nr-date", data.new_release.release_date)
      this.setVal(line, ".line-nr-spotify", data.new_release.spotify_url)
    }

    this.setVal(line, ".line-playlist-id", data.curator_playlist_id)
    this.setVal(line, ".line-playlist-via-url", data.playlist_via_url)
    if (data.other_curator_playlist) line.dataset.otherCuratorPlaylist = JSON.stringify(data.other_curator_playlist)
    if (data.new_playlist && Object.values(data.new_playlist).some(v => v)) {
      const panel = line.querySelector(".line-new-playlist")
      if (panel) panel.style.display = "block"
      this.setVal(line, ".line-np-name", data.new_playlist.name)
      this.setVal(line, ".line-np-link", data.new_playlist.spotify_url)
    }

    this.setVal(line, ".line-amount", data.amount)
    this.setVal(line, ".line-position", data.position)

    // Tripwire markers survive a server-rejected re-render (Seat B
    // reconvene B2): without this, a converted or confirmed-separate line
    // silently reverts and the server chokepoint traps the operator in a
    // refusal loop with no way to re-answer the modal.
    this.setVal(line, ".line-dup-separate", data.duplicate_confirmed_separate)
    if (data.renewal_of_placement_id) {
      this.setVal(line, ".line-renewal-of", data.renewal_of_placement_id)
      const notice = line.querySelector(".line-renewal-notice")
      const text = line.querySelector(".line-renewal-notice-text")
      if (text) text.textContent = "Recording as a renewal of the existing placement on this playlist."
      if (notice) notice.style.display = "block"
      const posLabel = line.querySelector(".line-position-label")
      if (posLabel) posLabel.textContent = "New position (optional)"
      const statusGroup = line.querySelector(".line-entry-status-group")
      if (statusGroup) statusGroup.style.display = "none"
    }

    const es = line.querySelector(".line-entry-status")
    if (es && data.entry_status) {
      es.value = data.entry_status
      this.revealEntryFields(line, data.entry_status)
      this.setVal(line, ".line-placed-date", data.placed_date)
      this.setVal(line, ".line-end-date", data.end_date)
      this.setVal(line, ".line-final-streams", data.final_streams)
    }
  }

  ensureHidden(line, field, value, className) {
    let el = line.querySelector(`[data-line-hidden="${field}"]`)
    if (!el) {
      el = document.createElement("input")
      el.type = "hidden"
      el.name = `lines[0][${field}]`
      el.setAttribute("data-line-hidden", field)
      if (className) el.className = className
      line.querySelector(".spt-card-body").prepend(el)
    }
    el.value = value
  }

  setVal(line, selector, value) {
    const el = line.querySelector(selector)
    if (el && value !== null && value !== undefined) el.value = value
  }

  // ----- Payment type -----

  typeChanged() { this.applyTypeLock(); this.refreshAllocation() }

  isZeroType() {
    if (!this.hasTypeSelectTarget) return false
    return ["free_placement", "playlist_trade", "submithub"].includes(this.typeSelectTarget.value)
  }

  applyTypeLock() {
    if (this.isPitched()) return
    if (!this.hasTotalAmountTarget) return
    if (this.isZeroType()) {
      this.totalAmountTarget.value = "0"
      this.totalAmountTarget.readOnly = true
      this.lineAmountInputs().forEach(i => { i.value = "0"; i.readOnly = true })
    } else {
      this.totalAmountTarget.readOnly = false
    }
  }

  // ----- Curator search -----

  filterCurators() {
    if (!this.hasCuratorListTarget) return
    const query = this.curatorSearchTarget.value.toLowerCase()
    const matches = query.length === 0
      ? this.curatorsValue
      : this.curatorsValue.filter(c => c.name.toLowerCase().includes(query))

    if (matches.length === 0) {
      this.curatorListTarget.innerHTML = `<div class="campaign-client-option" style="color: var(--spt-text-secondary);">No matches</div>`
    } else {
      this.curatorListTarget.innerHTML = matches.slice(0, 50).map(c =>
        `<div class="campaign-client-option" data-action="click->payment-form#selectCurator" data-curator-id="${c.id}">${this.esc(c.name)}</div>`
      ).join("")
    }
    this.curatorListTarget.style.display = "block"
  }

  selectCurator(event) {
    event.stopPropagation()
    const id = parseInt(event.currentTarget.dataset.curatorId)
    const curator = this.curatorsValue.find(c => c.id === id)
    if (!curator) return

    this.curatorSearchTarget.value = curator.name
    this.curatorIdTarget.value = curator.id
    this.curatorListTarget.style.display = "none"
    this.renderCuratorNote(curator)

    // Clear stale playlist state before the fetch resolves.
    this.clearAllPlaylistState()
    this.loadPlaylists(curator.id)
    this.recomputeBudgetWarning()
    // A curator paid in EUR preselects EUR on the amount control while the
    // amount is still blank (owner directive 2026-09-09).
    const fx = this.element.querySelector("[data-controller~='spt-fx-amount']")
    if (fx && curator.currency) fx.dispatchEvent(new CustomEvent("fx-amount:preselect", { detail: { currency: curator.currency } }))
  }

  // The curator note slot, ONE composer for both of its lines, in BOTH
  // pitched and accepted modes. Not-in-catalog is a hard check (red):
  // picking the wrong curator should be caught even on a pitch. The follower
  // watch's pitch sentence (2026-10-08, amber: the curator is fine) is what
  // Hannah adds when she pitches them: a pitch carries no playlist, so this
  // is the one place the form can say it at that moment.
  renderCuratorNote(curator) {
    if (!this.hasCatalogNoteTarget) return
    const parts = []
    if (curator && curator.current_catalog === false) {
      parts.push(`<span style="color: var(--spt-error); font-weight: 600;">This curator is not in SPT's Active Catalog. Please ensure you have the correct curator name.</span>`)
    }
    if (curator && curator.avoid_pitch) {
      parts.push(`<div class="line-avoid-pitch" style="color: #92400E; margin-top: 4px;">${this.esc(curator.avoid_pitch)} ` +
        `<button type="button" class="spt-btn-ghost spt-btn-sm" data-controller="copy-to-clipboard" data-action="copy-to-clipboard#copy" ` +
        `data-copy-to-clipboard-text-value="${this.esc(curator.avoid_fyi || "")}">Copy</button></div>`)
    }
    this.catalogNoteTarget.innerHTML = parts.join("")
    this.catalogNoteTarget.style.display = parts.length ? "block" : "none"
  }

  // A line's client: pinned on the line, else its picked song's.
  lineClientId(line) {
    const pinned = line.querySelector(".line-client-id")?.value
    if (pinned) return pinned
    const releaseId = line.querySelector(".line-release-id")?.value
    const release = releaseId ? (this.releasesValue || []).find(r => String(r.id) === String(releaseId)) : null
    return release ? String(release.client_id) : ""
  }

  // On a pitch there is no playlist to badge, so the note under the curator
  // names PR's playlists for the clients on this form (publicity
  // placements plan; workflow seat: never another client's).
  renderPublicityNote() {
    if (!this.hasPublicityNoteTarget) return
    const clients = new Set([...this.element.querySelectorAll(".payment-line")].map(l => this.lineClientId(l)).filter(Boolean))
    const pitched = this.isPitched()
    const lines = !pitched ? [] : (this.playlists || []).flatMap(pl =>
      (pl.active_placements || []).filter(a => clients.has(String(a.client_id))).map(a => a.pitch_note)
        .concat((pl.publicity || []).filter(p => clients.has(String(p.client_id))).map(p => p.named)))
    const html = lines.map(text => `<div class="spt-badge spt-badge-warning" style="display: block; margin-bottom: 4px; border-radius: 6px; white-space: normal;">${this.esc(text)}</div>`)
    // Add it above the older songs at the PITCH (workflow seat M1): a pitch
    // names no playlist, so every playlist of this curator's still holding
    // TREND songs past their month gets its box here, except one marked to
    // avoid (the red avoid sentence above says to leave it out).
    if (pitched) (this.playlists || []).filter(pl => pl.above_ask && !pl.avoid).forEach(pl => html.push(this.aboveAskHtml(pl.above_ask, "pitch-above-ask")))
    this.publicityNoteTarget.innerHTML = html.join("")
    this.publicityNoteTarget.style.display = html.length ? "block" : "none"
  }

  clearAllPlaylistState() {
    this.playlists = []
    this.element.querySelectorAll(".payment-line").forEach(line => {
      delete line.dataset.otherCuratorPlaylist
      const hidden = line.querySelector(".line-playlist-id")
      if (hidden) hidden.value = ""
      const viaUrl = line.querySelector(".line-playlist-via-url")
      if (viaUrl) viaUrl.value = ""
      const select = line.querySelector(".line-playlist-select")
      if (select) select.selectedIndex = 0
      const badge = line.querySelector(".line-concurrency")
      if (badge) { badge.style.display = "none"; badge.innerHTML = "" }
      const np = line.querySelector(".line-new-playlist")
      if (np) np.style.display = "none"
      const urlInput = line.querySelector(".line-playlist-url")
      if (urlInput) { urlInput.value = ""; delete urlInput.dataset.pendingUrl }
      this.clearRenewalMarkers(line)
      this.clearLineResolve(line)
    })
  }

  closeDropdowns(event) {
    if (this.hasCuratorListTarget && !this.curatorListTarget.contains(event.target) &&
        (!this.hasCuratorSearchTarget || event.target !== this.curatorSearchTarget)) {
      this.curatorListTarget.style.display = "none"
    }
    this.element.querySelectorAll(".line-release-list").forEach(list => {
      const line = list.closest(".payment-line")
      const search = line.querySelector(".line-release-search")
      if (!list.contains(event.target) && event.target !== search) list.style.display = "none"
    })
  }

  // ----- Playlists -----

  loadPlaylists(curatorId, callback) {
    const url = this.playlistsUrlValue.replace("CURATOR_ID", curatorId)
    fetch(url, { headers: { "Accept": "application/json" } })
      .then(r => r.json())
      .then(data => { this.playlists = data; this.fillPlaylistSelects(); if (callback) callback(); this.renderPublicityNote() })
      .catch(() => { this.playlists = []; if (callback) callback(); this.renderPublicityNote() })
  }

  fillPlaylistSelects() {
    this.element.querySelectorAll(".line-playlist-select").forEach(select => {
      const current = select.value
      const options = [`<option value="">Select a playlist...</option>`]
      this.playlists.forEach(pl => {
        // Follower watch: a marked playlist says so before it is picked.
        // One pair of brackets (design seat N2): "(avoid, on file under B)".
        const notes = [pl.avoid ? "avoid" : null, pl.other_curator_name ? `on file under ${this.esc(pl.other_curator_name)}` : null].filter(Boolean)
        options.push(`<option value="${pl.id}">${this.esc(pl.name)}${notes.length ? ` (${notes.join(", ")})` : ""}</option>`)
      })
      options.push(`<option value="__new__">+ Add new playlist</option>`)
      select.innerHTML = options.join("")
      if (current) select.value = current
    })
  }

  restorePlaylistSelections() {
    // A saved line on another curator's playlist: the paid curator's own list
    // does not hold it, so add it (named) before the selects are set.
    let added = false
    this.element.querySelectorAll(".payment-line").forEach(line => {
      if (!line.dataset.otherCuratorPlaylist) return
      let pl
      try { pl = JSON.parse(line.dataset.otherCuratorPlaylist) } catch (_) { return }
      if (pl && pl.id && !this.playlists.find(p => String(p.id) === String(pl.id))) {
        // The server's whole entry (PlaylistFormEntries): avoid mark, "add it
        // above" box and renewal question come with it (design S5, red team S4).
        this.playlists.push({ ...pl, other_curator_name: pl.other_curator_name || "another curator" })
        added = true
      }
      if (pl) this.showLineResolveHtml(line, this.otherCuratorNote(pl.other_curator_name))
    })
    if (added) this.fillPlaylistSelects()
    this.element.querySelectorAll(".payment-line").forEach(line => {
      const plId = line.querySelector(".line-playlist-id")
      const select = line.querySelector(".line-playlist-select")
      const badge = line.querySelector(".line-concurrency")
      if (plId && plId.value && select) {
        select.value = plId.value
        this.renderConcurrency(badge, plId.value)
      }
    })
  }

  // ----- Per-line release search -----

  filterReleases(event) {
    const input = event.target
    const line = input.closest(".payment-line")
    const list = line.querySelector(".line-release-list")
    const q = input.value.toLowerCase()
    // When the line's client is known (existing/imported line), show only that
    // client's releases. A fresh line with no client yet shows all releases. The
    // "+ Add new release" button stays available either way.
    const clientId = line.querySelector(".line-client-id")?.value
    let pool = this.releasesValue
    if (clientId) pool = pool.filter(r => String(r.client_id) === String(clientId))
    const matches = (q.length === 0 ? pool : pool.filter(r => r.label.toLowerCase().includes(q))).slice(0, 50)

    if (matches.length === 0) {
      list.innerHTML = `<div class="campaign-client-option" style="color: var(--spt-text-secondary);">No matches</div>`
    } else {
      list.innerHTML = matches.map(r =>
        `<div class="campaign-client-option" data-action="click->payment-form#selectRelease" data-release-id="${r.id}" data-release-label="${this.esc(r.label)}">${this.esc(r.label)}</div>`
      ).join("")
    }
    list.style.display = "block"
  }

  selectRelease(event) {
    event.stopPropagation()
    const opt = event.currentTarget
    const line = opt.closest(".payment-line")
    line.querySelector(".line-release-search").value = opt.dataset.releaseLabel
    line.querySelector(".line-release-id").value = opt.dataset.releaseId
    line.querySelector(".line-release-list").style.display = "none"
    const panel = line.querySelector(".line-new-release")
    if (panel) panel.style.display = "none"
    this.recomputeBudgetWarning()
    this.recomputePlaylistWarning()
    this.checkRenewalTripwire(line)
    // The PR warning is about this song's client: redraw it now it is known.
    this.renderConcurrency(line.querySelector(".line-concurrency"), line.querySelector(".line-playlist-id")?.value)
    this.renderPublicityNote()
  }

  // ----- Renewal tripwire (renewal rail, 2026-08-11) -----
  // When a line's playlist + release match an existing accepted placement
  // (any accepted status INCLUDING expired: monthly re-buys renew expired
  // rows), ask whether this money renews it. "Yes" converts the line in
  // place (hidden renewal_of_placement_id, purple notice, position
  // relabeled, entry status hidden); "No" marks it confirmed-separate. Any
  // later playlist/release change clears both markers (the via-URL marker
  // discipline) and re-asks. The server re-verifies the tuple at submit.

  checkRenewalTripwire(line) {
    this.clearRenewalMarkers(line)
    const relId = line.querySelector(".line-release-id")?.value
    const plId = line.querySelector(".line-playlist-id")?.value
    if (!relId || !plId) return

    const pl = this.playlists.find(p => String(p.id) === String(plId))
    // The row this payment can renew first (red team round 2 S1): with the
    // same song on the list through two curators, the one paid through this
    // payment's curator is the one to ask about.
    const sameRelease = (pl && pl.accepted_placements || []).filter(a => String(a.shared_release_id) === String(relId))
    const match = sameRelease.find(a => a.renewable) || sameRelease[0]
    if (!match) return

    const buttons = []
    if (match.renewable) {
      buttons.push({ label: "Yes, it is a renewal", variant: "ghost", onClick: () => this.convertLineToRenewal(line, match, pl) })
    }
    buttons.push({ label: match.through_name ? "Record as a separate placement" : "No, separate placement", variant: "ghost", onClick: () => {
      const sep = line.querySelector(".line-dup-separate")
      if (sep) sep.value = "1"
    } })
    buttons.push({ label: "Cancel", variant: "primary", focus: true, onClick: () => this.clearLinePlaylist(line) })

    const placed = match.placed_date ? `placed ${match.placed_date}` : "recently recorded"
    // Paid through another curator (Cross_Curator_Placement_Plan.md, red team
    // M1): it renews only from a payment to that curator, so no "Yes" here.
    const message = match.through_name
      ? `${pl.name} already has a placement for this release: ${match.client_name}, ${placed} for $${match.amount.toFixed(2)}, paid through ${match.through_name}. Only a payment to ${match.through_name} can renew it (use Renew on that row of the Placements page). If this payment is a new placement, record it as a separate one.`
      : `${pl.name} already has a placement for this release: ${match.client_name}, ${placed} for $${match.amount.toFixed(2)}. Is this a renewal payment for that placement?`
    this.openModal({ title: "Already recorded for this release", message, buttons })
  }

  convertLineToRenewal(line, match, pl) {
    const marker = line.querySelector(".line-renewal-of")
    if (marker) marker.value = match.placement_id
    const sep = line.querySelector(".line-dup-separate")
    if (sep) sep.value = ""

    const notice = line.querySelector(".line-renewal-notice")
    const text = line.querySelector(".line-renewal-notice-text")
    if (text) text.textContent = `Recording as a renewal of the ${match.placed_date || "existing"} placement on ${pl.name}.`
    if (notice) notice.style.display = "block"

    const posLabel = line.querySelector(".line-position-label")
    if (posLabel) posLabel.textContent = "New position (optional)"
    const statusGroup = line.querySelector(".line-entry-status-group")
    if (statusGroup) statusGroup.style.display = "none"
    this.redrawLineBadges(line)
  }

  undoRenewalMark(event) {
    const line = event.target.closest(".payment-line")
    this.clearRenewalMarkers(line)
  }

  clearRenewalMarkers(line) {
    if (!line) return
    const marker = line.querySelector(".line-renewal-of")
    const sep = line.querySelector(".line-dup-separate")
    if (marker) marker.value = ""
    if (sep) sep.value = ""
    const notice = line.querySelector(".line-renewal-notice")
    if (notice) notice.style.display = "none"
    const posLabel = line.querySelector(".line-position-label")
    if (posLabel) posLabel.textContent = "Original position"
    const statusGroup = line.querySelector(".line-entry-status-group")
    if (statusGroup && !this.isPitched()) statusGroup.style.display = ""
    this.redrawLineBadges(line)
  }

  clearLinePlaylist(line) {
    const select = line.querySelector(".line-playlist-select")
    const hidden = line.querySelector(".line-playlist-id")
    if (select) select.selectedIndex = 0
    if (hidden) hidden.value = ""
    const badge = line.querySelector(".line-concurrency")
    if (badge) { badge.style.display = "none"; badge.innerHTML = "" }
  }

  toggleNewRelease(event) {
    const line = event.target.closest(".payment-line")
    const panel = line.querySelector(".line-new-release")
    if (!panel) return
    const show = panel.style.display === "none"
    panel.style.display = show ? "block" : "none"
    if (show) {
      line.querySelector(".line-release-id").value = ""
      line.querySelector(".line-release-search").value = ""
      this.clearRenewalMarkers(line)
    }
  }

  // When a new release's date is more than 7 days before today, ask whether it is a
  // legacy/warm-up track (its 8-week goal then measures from first placement, not
  // release date). Within 7 days: not legacy, no popup. Same threshold as the
  // release form's effective-campaign-date prompt (SharedRelease::STALE_AFTER_DAYS).
  checkNewReleaseLegacy(event) {
    const input = event.target
    const line = input.closest(".payment-line")
    const legacyField = line.querySelector(".line-nr-legacy")
    if (legacyField) legacyField.value = "0"
    if (!input.value) return

    const released = new Date(input.value + "T00:00:00")
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7)
    if (released >= weekAgo) return

    this.openModal({
      title: "Older release: treat as a legacy track?",
      message: "This song was released more than a week ago. Treat it as a legacy/warm-up track? If yes, SPT measures its 8-week streaming goal from when we first place it, not from its release date.",
      buttons: [
        { label: "Yes, legacy track", variant: "ghost", onClick: () => { if (legacyField) legacyField.value = "1" } },
        { label: "No, new release", variant: "primary", focus: true, onClick: () => { if (legacyField) legacyField.value = "0" } }
      ]
    })
  }

  // ----- Per-line playlist -----

  playlistChanged(event) {
    const select = event.target
    const line = select.closest(".payment-line")
    const hidden = line.querySelector(".line-playlist-id")
    const viaUrl = line.querySelector(".line-playlist-via-url")
    const panel = line.querySelector(".line-new-playlist")
    const badge = line.querySelector(".line-concurrency")

    // A manual dropdown pick is a this-curator selection, never a URL tie: drop the
    // via-URL marker so the relaxed server guard applies only to actual URL pastes.
    // The one exception is a playlist another curator has on file that a paste
    // (or the saved line) already put in the list: picking it again is the same
    // confirmed tie, so it keeps the marker and says where it stays.
    const picked = this.playlists.find(p => String(p.id) === String(select.value))
    const other = picked && picked.other_curator_name
    if (viaUrl) viaUrl.value = other ? "1" : ""
    this.clearLineResolve(line)
    this.clearRenewalMarkers(line)
    if (other) this.showLineResolveHtml(line, this.otherCuratorNote(other))

    if (select.value === "__new__") {
      hidden.value = ""
      if (panel) panel.style.display = "block"
      if (badge) { badge.style.display = "none"; badge.innerHTML = "" }
      this.recomputePlaylistWarning()
      return
    }

    hidden.value = select.value
    if (panel) panel.style.display = "none"
    this.renderConcurrency(badge, select.value)
    this.recomputePlaylistWarning()
    this.checkRenewalTripwire(line)
  }

  // Every path that ties a line to a playlist reaches this (dropdown, paste,
  // the re-render restore), so the follower watch's red line is drawn here
  // and survives a rejected submit without asking again.
  renderConcurrency(badge, playlistId) {
    if (!badge) return
    const pl = this.playlists.find(p => String(p.id) === String(playlistId))
    const active = pl ? (pl.active_placements || []) : []
    const line = badge.closest(".payment-line")
    const html = []
    if (pl && pl.avoid) {
      html.push(`<div class="spt-badge spt-badge-error line-avoid-badge" style="display: block; margin-bottom: 4px; border-radius: 6px; white-space: normal;">Avoid this playlist. ${this.esc(pl.avoid.reason)}</div>`)
    }
    // Add it above the older songs (SPT_Planner_Above_Plan.md), only on a
    // line that puts a new song on this playlist.
    if (pl && pl.above_ask && this.addsNewSong(line, playlistId)) html.push(this.aboveAskHtml(pl.above_ask, "line-above-ask"))
    // The songs in their month, one badge each (they count toward the
    // ceiling); the ones past it fold into one line that opens to the list.
    const when = a => a.placed_date ? `placed on ${this.esc(a.placed_date)}` : "(pending)"
    const past = active.filter(a => a.past_term)
    active.filter(a => !a.past_term).forEach(a => {
      const until = a.likely_until ? `, likely active until ${this.esc(a.likely_until)}` : ""
      html.push(`<div class="spt-badge spt-badge-warning" style="display: block; margin-bottom: 4px;">Active TREND placement: ${this.esc(a.client_name)} ${when(a)}${until}</div>`)
    })
    if (past.length) {
      const rows = past.map(a => `<div style="margin-top: 4px;">${this.esc(a.client_name)} ${when(a)}${a.likely_until ? `, its month ended ${this.esc(a.likely_until)}` : ""}</div>`).join("")
      html.push(`<details class="spt-badge spt-badge-warning line-past-term" style="display: block; margin-bottom: 4px; border-radius: 6px; white-space: normal;">` +
        `<summary style="cursor: pointer;">${past.length} TREND placement${past.length === 1 ? "" : "s"} past ${past.length === 1 ? "its" : "their"} month</summary>${rows}</details>`)
    }
    // PR already has this line's client on this playlist and it is not a
    // placement yet (publicity placements plan): never the clip this line
    // is counting, never another client's.
    const clientId = line ? this.lineClientId(line) : ""
    const ownClip = line?.querySelector(".line-press-clip-id")?.value || ""
    const publicity = pl ? (pl.publicity || []) : []
    publicity.filter(p => clientId && String(p.client_id) === String(clientId) && String(p.clip_id) !== String(ownClip)).forEach(p => {
      html.push(`<div class="spt-badge spt-badge-warning line-publicity-badge" style="display: block; margin-bottom: 4px; border-radius: 6px; white-space: normal;">${this.esc(p.here)}</div>`)
    })
    badge.innerHTML = html.join("")
    badge.style.display = html.length ? "block" : "none"
  }

  redrawLineBadges(line) {
    if (!line) return
    this.renderConcurrency(line.querySelector(".line-concurrency"), line.querySelector(".line-playlist-id")?.value)
  }

  // Does this line put a NEW song on the playlist? Not when Count it is
  // counting a PR add (the song is already there), not a renewal, not a
  // Declined or Expired entry, and not a saved line still on the playlist
  // it was saved with (a saved line getting its first playlist, or a new
  // one, still asks). Review seats 1, 2 and 3, 2026-10-21.
  addsNewSong(line, playlistId) {
    if (!line) return true
    if (line.querySelector(".line-press-clip-id")?.value) return false
    if (line.querySelector(".line-renewal-of")?.value) return false
    if (["declined", "expired"].includes(line.querySelector(".line-entry-status")?.value)) return false
    const saved = line.dataset.savedPlaylist
    return !(saved && String(saved) === String(playlistId))
  }

  // THE drawing of the "add it above the older songs" box on this form
  // (SPT_Planner_Above_Plan.md; owner 2026-10-21: "make sure the part about
  // ensuring the playlists go above the others is highlighted so she can't
  // miss it"), for a line and for the Pitched-mode note alike. The campaign
  // card, the curator page and the Resolve panel draw the same box from
  // departments/spt/shared/_above_ask.html.erb: change them together.
  aboveAskHtml(ask, extraClass) {
    return `<div class="spt-above-ask ${extraClass}" style="margin-bottom: 8px; padding: 8px 12px; background: #FFFBEB; border-left: 4px solid #F59E0B; border-radius: 6px; color: #92400E; font-weight: 600; white-space: normal;">` +
      `${this.esc(ask.line)} <button type="button" class="spt-btn-secondary spt-btn-sm" data-controller="copy-to-clipboard" data-action="copy-to-clipboard#copy" ` +
      `data-copy-to-clipboard-text-value="${this.esc(ask.copy)}">Copy</button></div>`
  }

  // ----- Per-line playlist-by-URL -----

  // Fires on blur (change). Paste schedules the same call once the value lands.
  resolvePlaylistUrl(event) { this.doResolvePlaylist(event.target) }

  resolvePlaylistUrlPaste(event) {
    const input = event.target
    setTimeout(() => this.doResolvePlaylist(input), 0)
  }

  doResolvePlaylist(input) {
    const line = input.closest(".payment-line")
    const url = input.value.trim()
    if (!url) { this.clearLineResolve(line); return }
    // Dedupe paste+blur firing for the same text.
    if (input.dataset.pendingUrl === url) return
    input.dataset.pendingUrl = url

    const curatorId = this.hasCuratorIdTarget ? this.curatorIdTarget.value : null
    if (!curatorId) {
      this.showLineResolve(line, "spt-badge-warning", "Select a curator first, then paste the playlist URL.")
      delete input.dataset.pendingUrl
      return
    }
    if (!this.hasResolveUrlValue) return

    this.showLineResolve(line, "spt-helper", "Looking up playlist...")
    const endpoint = this.resolveUrlValue.replace("CURATOR_ID", curatorId)
    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.csrfToken() },
      body: JSON.stringify({ url })
    })
      .then(r => r.json())
      .then(data => this.handleResolveResult(line, data))
      .catch(() => this.showLineResolve(line, "spt-badge-warning", "Could not reach Spotify to look up this playlist. Try again, or pick from the dropdown."))
  }

  handleResolveResult(line, data) {
    switch (data && data.status) {
      case "resolved":
        this.tieOrAskAvoid(line, data, false)
        break
      case "cross_curator":
        this.openCrossCuratorModal(line, data)
        break
      case "invalid_url":
      case "not_found":
        this.openInvalidLinkModal(line, data)
        break
      default:
        this.showLineResolve(line, "spt-badge-warning", "Could not look up this playlist. Pick from the dropdown or add it manually.")
    }
  }

  tieLineToPlaylist(line, data, crossCurator) {
    // The server's full entry when it sent one (PlaylistFormEntries): who is
    // on it and what renews through this curator come with it (red team S4).
    if (!this.playlists.find(p => String(p.id) === String(data.id))) {
      this.playlists.push(data.entry || { id: data.id, name: data.name, followers: data.followers, active_placements: [], avoid: data.avoid || null, above_ask: data.above_ask || null,
                                          other_curator_name: crossCurator ? (data.other_curator_name || "another curator") : null })
    }
    this.fillPlaylistSelects()

    const hidden = line.querySelector(".line-playlist-id")
    const viaUrl = line.querySelector(".line-playlist-via-url")
    const select = line.querySelector(".line-playlist-select")
    const np = line.querySelector(".line-new-playlist")
    if (hidden) hidden.value = data.id
    if (viaUrl) viaUrl.value = "1"
    if (select) select.value = String(data.id)
    if (np) np.style.display = "none"
    this.renderConcurrency(line.querySelector(".line-concurrency"), data.id)

    const followers = (data.followers === null || data.followers === undefined)
      ? "unknown" : Number(data.followers).toLocaleString()
    let html = `<span class="spt-badge spt-badge-success" style="display: block;">Linked: ${this.esc(data.name)} (${followers} followers)</span>`
    if (crossCurator) html += this.otherCuratorNote(data.other_curator_name, data.payer_name)
    this.showLineResolveHtml(line, html)
    this.recomputePlaylistWarning()
    this.checkRenewalTripwire(line)
  }

  // A playlist on file under another curator stays there when you save
  // (owner 2026-10-27, Cross_Curator_Placement_Plan.md); the placement is
  // recorded under the curator this payment is to.
  otherCuratorNote(otherName, payerName) {
    const other = this.esc(otherName || "another curator")
    const payer = this.esc(payerName || this.currentCuratorName())
    return `<span class="spt-helper" style="display: block; margin-top: 4px;">Stays on file under ${other}. This placement is recorded under ${payer}.</span>`
  }

  // The curator this payment is to, as typed in the Curator box (the avoid
  // modal reads it the same way). Never "the curator you paid": a free,
  // trade or SubmitHub line pays nobody (design seat S2).
  currentCuratorName() {
    return (this.hasCuratorSearchTarget && this.curatorSearchTarget.value) || "the curator above"
  }

  // Leaves the line with no playlist (awaiting_playlist) so it can be added later
  // from the Monday check-in; never ties a non-existent playlist.
  skipPlaylistForNow(line) {
    const hidden = line.querySelector(".line-playlist-id")
    const viaUrl = line.querySelector(".line-playlist-via-url")
    if (hidden) hidden.value = ""
    if (viaUrl) viaUrl.value = ""
    const urlInput = line.querySelector(".line-playlist-url")
    if (urlInput) { urlInput.value = ""; delete urlInput.dataset.pendingUrl }
    this.clearRenewalMarkers(line)
    this.showLineResolve(line, "spt-helper", "Saved without a playlist. It will appear in the Monday check-in's Missing Playlist Link list to add later.")
    this.recomputePlaylistWarning()
  }

  // Clears the paste so the user can correct the URL; no tie, no playlist.
  clearLinePaste(line) {
    const urlInput = line.querySelector(".line-playlist-url")
    if (urlInput) { urlInput.value = ""; delete urlInput.dataset.pendingUrl; urlInput.focus() }
    this.clearLineResolve(line)
  }

  // Design seat S1: TEAM knows whose list it is, so the title says so; the
  // question worth asking is whether the Curator box is the right curator.
  openCrossCuratorModal(line, data) {
    const other = data.other_curator_name || "another curator"
    const payer = data.payer_name || this.currentCuratorName()
    this.openModal({
      title: `Heads up, this playlist is on file under ${other}.`,
      message: `If you use it, it stays on file under ${other} and nothing moves. This placement is recorded under ${payer}. If you dealt with ${other} directly, cancel and choose ${other} in the Curator box.`,
      buttons: [
        { label: "Use this playlist", variant: "ghost", onClick: () => this.tieOrAskAvoid(line, data, true) },
        { label: "Cancel", variant: "primary", focus: true, onClick: () => this.clearLinePaste(line) }
      ]
    })
  }

  // Follower watch (2026-10-08): a pasted link to a playlist marked to avoid
  // asks once, AFTER the cross-curator question and BEFORE the renewal
  // tripwire (tieLineToPlaylist ends in checkRenewalTripwire), so modals
  // never stack. The dropdown path asks nothing: "(avoid)" was on screen.
  tieOrAskAvoid(line, data, crossCurator) {
    if (!data.avoid) { this.tieLineToPlaylist(line, data, crossCurator); return }
    const curatorName = (this.hasCuratorSearchTarget && this.curatorSearchTarget.value) || "the curator"
    this.openModal({
      title: "Heads up, this playlist is marked to avoid.",
      message: `${data.avoid.reason} If ${curatorName} already put the song on it, keep it and record the placement as it happened. If not, pick a different playlist.`,
      buttons: [
        { label: "Keep this playlist", variant: "ghost", onClick: () => this.tieLineToPlaylist(line, data, crossCurator) },
        { label: "Pick a different playlist", variant: "primary", focus: true, dismiss: true, onClick: () => this.clearLinePaste(line) }
      ]
    })
  }

  openInvalidLinkModal(line, data) {
    let message
    if (data.status === "not_found") {
      message = "Spotify could not return a playlist for that link. Double-check the URL, or the playlist may be private or unavailable."
    } else if (data.link_type) {
      message = `This looks like a ${data.link_type} link. Playlist links contain "/playlist/".`
    } else {
      message = `This does not look like a Spotify playlist link. Playlist links contain "/playlist/".`
    }
    this.openModal({
      title: "Heads up, this does not appear to be a direct Spotify playlist link.",
      message: message,
      buttons: [
        { label: "Save without a playlist for now", variant: "ghost", onClick: () => this.skipPlaylistForNow(line) },
        { label: "Cancel", variant: "primary", focus: true, onClick: () => this.clearLinePaste(line) }
      ]
    })
  }

  // Native in-app modal built from the app's existing .sh-modal styles. Default
  // focus goes to the button flagged focus:true (Cancel), so a fast Enter never
  // auto-confirms a possible mistake.
  openModal({ title, message, buttons }) {
    const overlay = document.createElement("div")
    overlay.className = "sh-modal"
    const backdrop = document.createElement("div")
    backdrop.className = "sh-modal-backdrop"
    const content = document.createElement("div")
    content.className = "sh-modal-content"

    const h = document.createElement("h3")
    h.className = "sh-modal-title"
    h.textContent = title
    const p = document.createElement("p")
    p.className = "sh-modal-message"
    p.textContent = message
    const actions = document.createElement("div")
    actions.className = "sh-modal-actions"

    const close = () => { overlay.remove(); document.removeEventListener("keydown", onKey) }
    // A button flagged dismiss: is what Escape and the backdrop do, so a
    // dismissed question never leaves a line half answered.
    const dismissBtn = buttons.find(b => b.dismiss)
    const dismiss = () => { close(); if (dismissBtn && dismissBtn.onClick) dismissBtn.onClick() }
    const onKey = (e) => { if (e.key === "Escape") dismiss() }

    let focusBtn = null
    buttons.forEach(b => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = `btn ${b.variant === "primary" ? "btn-primary" : "btn-ghost"}`
      btn.textContent = b.label
      btn.addEventListener("click", () => { close(); if (b.onClick) b.onClick() })
      actions.appendChild(btn)
      if (b.focus) focusBtn = btn
    })

    content.appendChild(h)
    content.appendChild(p)
    content.appendChild(actions)
    overlay.appendChild(backdrop)
    overlay.appendChild(content)
    backdrop.addEventListener("click", dismiss)
    document.addEventListener("keydown", onKey)
    document.body.appendChild(overlay)
    if (focusBtn) focusBtn.focus()
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }

  showLineResolve(line, klass, text) {
    const el = line.querySelector(".line-url-resolve")
    if (!el) return
    el.innerHTML = klass.startsWith("spt-badge")
      ? `<span class="spt-badge ${klass}" style="display: block;">${this.esc(text)}</span>`
      : `<span class="${klass}" style="display: block;">${this.esc(text)}</span>`
    el.style.display = "block"
  }

  showLineResolveHtml(line, html) {
    const el = line.querySelector(".line-url-resolve")
    if (!el) return
    el.innerHTML = html
    el.style.display = "block"
  }

  clearLineResolve(line) {
    const el = line.querySelector(".line-url-resolve")
    if (!el) return
    el.innerHTML = ""
    el.style.display = "none"
  }

  // ----- Per-line entry status -----

  entryStatusChanged(event) {
    const line = event.target.closest(".payment-line")
    this.revealEntryFields(line, event.target.value)
    if (event.target.value === "live") {
      const placed = line.querySelector(".line-placed-date")
      if (placed && !placed.value) placed.value = new Date().toISOString().slice(0, 10)
    }
    this.redrawLineBadges(line)
  }

  revealEntryFields(line, status) {
    const placedField = line.querySelector(".line-placed-field")
    const expiredExtra = line.querySelector(".line-expired-extra")
    const showPlaced = status === "live" || status === "expired"
    if (placedField) placedField.style.display = showPlaced ? "block" : "none"
    if (expiredExtra) expiredExtra.style.display = status === "expired" ? "block" : "none"
  }

  // ----- Lines -----

  toggleSplit() {
    const on = this.hasSplitToggleTarget && this.splitToggleTarget.checked
    if (this.hasAddLineRowTarget) this.addLineRowTarget.style.display = on ? "block" : "none"
    if (on) {
      if (this.activeLines().length < 2) this.addLine()
    } else {
      this.collapseToSingleLine()
    }
    this.refreshAllocation()
  }

  collapseToSingleLine() {
    this.activeLines().slice(1).forEach(line => {
      const destroy = line.querySelector(".line-destroy")
      if (destroy) { destroy.value = "1"; line.style.display = "none" } else { line.remove() }
    })
    this.reindex()
  }

  addLine() {
    if (!this.lineTemplate) return null
    const clone = this.lineTemplate.cloneNode(true)
    this.linesContainerTarget.appendChild(clone)
    // A line added while pitching shows only its release (no amount/playlist/status).
    if (this.isPitched()) clone.querySelectorAll(".line-accepted-only").forEach(el => { el.style.display = "none" })
    this.fillPlaylistSelects()
    this.reindex()
    this.refreshAllocation()
    this.recomputePlaylistWarning()
    return clone
  }

  // Same client + release as THIS line (the one whose button was clicked), a new
  // playlist, an even share of the same charge. This is an ordinary split line (same
  // allocation math); the only difference from + Add split placement is that it
  // auto-inherits the client and release from its own line so Telvin just adds the
  // playlist.
  addAdditionalPlaylist(event) {
    if (this.isPitched()) return
    const source = event.target.closest(".payment-line")
    if (!source) return

    // Identity to inherit. Prefer the source's release; fall back to its client id +
    // song, because imported lines have a client but no SharedRelease.
    const relId = source.querySelector(".line-release-id")?.value || ""
    const relLabel = source.querySelector(".line-release-search")?.value || ""
    let clientId = source.querySelector(".line-client-id")?.value || ""
    let song = source.querySelector(".line-song-name")?.value || ""
    if (relId) {
      const rel = this.releasesValue.find(r => String(r.id) === String(relId))
      if (rel) { clientId = String(rel.client_id); song = rel.song || song }
    }
    if (!relId && !clientId) {
      this.flashMessage("Pick a release on this line first, then log an additional playlist for it.")
      return
    }

    // Turn split on so the charge divides across the lines, exactly like the split
    // toggle / + Add split placement path.
    if (this.hasSplitToggleTarget && !this.splitToggleTarget.checked) {
      this.splitToggleTarget.checked = true
      if (this.hasAddLineRowTarget) this.addLineRowTarget.style.display = "block"
    }

    const clone = this.addLine()
    if (!clone) return
    // Show it right under the source line, not at the bottom of the form.
    source.insertAdjacentElement("afterend", clone)

    // Inherit the release when there is one; otherwise carry client id + song as
    // submitted copy-fields so the server builds a release-less placement.
    if (clone.querySelector(".line-release-id")) clone.querySelector(".line-release-id").value = relId
    if (clone.querySelector(".line-release-search")) clone.querySelector(".line-release-search").value = relLabel
    if (clone.querySelector(".line-client-id")) clone.querySelector(".line-client-id").value = clientId
    if (clone.querySelector(".line-song-name")) clone.querySelector(".line-song-name").value = song
    clone.querySelector(".line-additional-playlist").value = "1"
    clone.querySelector(".line-copy-client-id").value = clientId
    clone.querySelector(".line-copy-song-name").value = song
    // The source placement's id (existing line) so the new line inherits its status.
    const srcId = source.querySelector("[data-line-hidden='id']")?.value || ""
    if (clone.querySelector(".line-copy-from-id")) clone.querySelector(".line-copy-from-id").value = srcId

    const identity = source.querySelector(".line-identity-text")?.textContent || ""
    if (identity) {
      const txt = clone.querySelector(".line-identity-text")
      const wrap = clone.querySelector(".line-identity")
      if (txt) txt.textContent = identity
      if (wrap) wrap.style.display = "block"
    }

    this.reindex() // re-number lines[] after moving the clone next to its source
    this.refreshAllocation()
    this.recomputeBudgetWarning()
  }

  removeLine(event) {
    const line = event.target.closest(".payment-line")
    // Never collapse to a zero-line state; keep at least one line present.
    if (this.activeLines().length <= 1) {
      this.flashMessage("At least one line is required to record a placement.")
      return
    }
    const destroy = line.querySelector(".line-destroy")
    if (destroy) { destroy.value = "1"; line.style.display = "none" } else { line.remove() }
    this.reindex()
    this.refreshAllocation()
    this.recomputeBudgetWarning()
    this.recomputePlaylistWarning()
  }

  reindex() {
    this.element.querySelectorAll(".payment-line").forEach((line, i) => {
      line.querySelectorAll("[name]").forEach(el => {
        el.name = el.name.replace(/^lines\[\d+\]/, `lines[${i}]`)
      })
    })
  }

  // ----- Allocation -----

  activeLines() {
    return Array.from(this.element.querySelectorAll(".payment-line")).filter(line => {
      const d = line.querySelector(".line-destroy")
      return !(d && d.value === "1") && line.style.display !== "none"
    })
  }

  lineAmountInputs() {
    return this.activeLines().map(l => l.querySelector(".line-amount")).filter(Boolean)
  }

  amountChanged() { this.refreshAllocation(); this.recomputeBudgetWarning() }
  totalChanged() { this.refreshAllocation(); this.recomputeBudgetWarning(); this.refreshPackageNote() }

  // ----- Package deal (owner directive 2026-09-09) -----

  packageMonthsCount() {
    if (!this.hasPackageToggleTarget || !this.packageToggleTarget.checked) return 1
    return parseInt(this.packageMonthsTarget.value, 10) || 1
  }

  togglePackage() {
    const on = this.packageToggleTarget.checked
    this.packageFieldsTarget.style.display = on ? "" : "none"
    // A disabled select never submits, so unchecked means term_months = 1.
    this.packageMonthsTarget.disabled = !on
    this.refreshPackageNote()
    this.recomputeBudgetWarning()
  }

  packageChanged() { this.refreshPackageNote(); this.recomputeBudgetWarning() }

  refreshPackageNote() {
    if (!this.hasPackageNoteTarget) return
    const months = this.packageMonthsCount()
    if (months <= 1) { this.packageNoteTarget.textContent = ""; return }
    const total = parseFloat(this.hasTotalAmountTarget ? this.totalAmountTarget.value : "") || 0
    let note = total > 0
      ? `Split evenly for budget tracking: $${(total / months).toFixed(2)} a month for ${months} months, starting the payment month.`
      : "Split evenly across the months for budget tracking."
    if (this.isSplitOn()) note += " Every line on this payment gets the same term. For a different term per song, record two payments."
    this.packageNoteTarget.textContent = note
  }

  isSplitOn() { return this.hasSplitToggleTarget && this.splitToggleTarget.checked }

  refreshAllocation() {
    if (this.isPitched()) { this.setAllocation(null); return }
    const total = parseFloat(this.hasTotalAmountTarget ? this.totalAmountTarget.value : "") || 0
    const inputs = this.lineAmountInputs()

    if (!this.isSplitOn()) {
      this.showAmountFields(false)
      if (inputs[0]) inputs[0].value = (this.isZeroType() ? 0 : total).toFixed(2)
      this.clearImbalanceMarks(inputs)
      this.setAllocation(null)
      return
    }

    this.showAmountFields(true)
    if (this.isZeroType()) inputs.forEach(i => { i.value = "0"; i.readOnly = true })
    else inputs.forEach(i => { i.readOnly = false })

    const allocated = inputs.reduce((s, i) => s + (parseFloat(i.value) || 0), 0)
    const diff = Math.round((allocated - total) * 100) / 100
    const t = total.toFixed(2)
    const a = allocated.toFixed(2)
    const gap = Math.abs(diff).toFixed(2)

    if (this.isZeroType() || Math.abs(diff) <= 0.005) {
      this.setAllocation("Split lines match the total.", true)
      this.clearImbalanceMarks(inputs)
    } else if (diff > 0) {
      this.setAllocation(`Your stated total is $${t}, but the split lines add up to $${a}, which is $${gap} over. Please adjust so they match.`, false)
      inputs.forEach(i => i.classList.add("imbalanced"))
    } else {
      this.setAllocation(`Your stated total is $${t}, but the split lines add up to $${a}, which leaves $${gap} unallocated. Please assign the rest.`, false)
      inputs.forEach(i => i.classList.add("imbalanced"))
    }
  }

  showAmountFields(show) {
    this.element.querySelectorAll(".line-amount-wrap").forEach(w => { w.style.display = show ? "block" : "none" })
  }

  clearImbalanceMarks(inputs) { inputs.forEach(i => i.classList.remove("imbalanced")) }

  setAllocation(text, ok) {
    if (!this.hasAllocationTarget) return
    if (text === null) { this.allocationTarget.style.display = "none"; return }
    this.allocationTarget.style.display = "block"
    this.allocationTarget.textContent = text
    this.allocationTarget.className = ok ? "spt-helper" : "spt-helper spt-helper-warning"
  }

  // ----- Over-budget soft warning -----

  recomputeBudgetWarning() {
    if (!this.hasBudgetWarningTarget) return
    if (this.isPitched()) { this.budgetWarningTarget.style.display = "none"; return }
    const byClient = {}
    this.activeLines().forEach(line => {
      const amt = parseFloat(line.querySelector(".line-amount")?.value) || 0
      if (amt <= 0) return
      // Resolve the line's client from its release, or from its stored client id
      // (imported lines have a client but no release). Keyed as a string so a
      // release-backed line and an imported line for the same client aggregate.
      let clientId = null
      let clientName = null
      const relId = line.querySelector(".line-release-id")?.value
      if (relId) {
        const rel = this.releasesValue.find(r => String(r.id) === String(relId))
        if (rel) { clientId = String(rel.client_id); clientName = rel.client_name }
      }
      if (!clientId) {
        const cid = line.querySelector(".line-client-id")?.value
        if (cid) clientId = String(cid)
      }
      if (!clientId) return
      byClient[clientId] = byClient[clientId] || { name: clientName, amount: 0 }
      byClient[clientId].amount += amt
    })

    // Only warn when this submit INCREASES a client's spend beyond what is already
    // committed on this payment. committed is empty on create (every dollar is new),
    // so the create flow is unchanged; on edit, a no-money change has delta 0 and
    // stays silent, and a client the edit does not touch is never reached.
    const committed = this.hasEditingCommittedValue ? this.editingCommittedValue : {}
    const msgs = []
    Object.keys(byClient).forEach(cid => {
      const b = this.clientBudgetsValue[cid]
      if (!b) return
      // A package lands one month's share in this window, not the whole
      // transfer (the server posts it the same way).
      const entered = byClient[cid].amount / this.packageMonthsCount()
      const already = parseFloat(committed[cid]) || 0
      const delta = Math.round((entered - already) * 100) / 100
      if (delta <= 0) return // not an increase: nothing new to warn about
      const projected = (b.spent || 0) + entered
      if (projected > b.budget) {
        const over = Math.round((projected - b.budget) * 100) / 100
        msgs.push(`Recording this will put ${b.name || byClient[cid].name} $${over.toFixed(2)} over their monthly budget (this change adds $${delta.toFixed(2)}). Proceed?`)
      }
    })

    if (msgs.length) {
      this.budgetWarningTarget.innerHTML = msgs.map(m =>
        `<span class="spt-badge spt-badge-warning" style="display: block; margin-bottom: 4px;">${this.esc(m)}</span>`
      ).join("")
      this.budgetWarningTarget.style.display = "block"
    } else {
      this.budgetWarningTarget.style.display = "none"
      this.budgetWarningTarget.innerHTML = ""
    }
  }

  // ----- No-playlist soft warning (never blocks) -----

  recomputePlaylistWarning() {
    if (!this.hasPlaylistWarningTarget) return
    if (this.isPitched()) { this.playlistWarningTarget.style.display = "none"; return }
    const awaiting = this.activeLines().some(line => {
      const hasRelease = (line.querySelector(".line-release-id")?.value) || (line.querySelector(".line-nr-song")?.value)
      const hasPlaylist = (line.querySelector(".line-playlist-id")?.value) || (line.querySelector(".line-np-name")?.value)
      return hasRelease && !hasPlaylist
    })
    if (awaiting) {
      this.playlistWarningTarget.innerHTML = `<span class="spt-badge spt-badge-warning" style="display: block;">No playlist selected. This placement will be flagged as Missing Playlist Link until one is added.</span>`
      this.playlistWarningTarget.style.display = "block"
    } else {
      this.playlistWarningTarget.style.display = "none"
      this.playlistWarningTarget.innerHTML = ""
    }
  }

  // ----- Submit guard (client-side blocks + imbalance) -----

  onSubmit(event) {
    // Double-submit guard (register 2026-08-05, item 3 root cause): the
    // 2026-07-22 duplicate rows were one form submitted twice 3.5 seconds
    // apart. Once a valid submit is in flight, refuse a second and disable
    // the button (disabling inside the submit handler does not stop the
    // in-flight submission).
    if (this.submitting) { event.preventDefault(); return }
    const problem = this.firstClientSideProblem()
    if (problem) { event.preventDefault(); this.flashMessage(problem); return }
    if (!this.isPitched() && this.isImbalanced()) { event.preventDefault(); this.flashAllocation(); return }
    this.submitting = true
    if (this.hasSubmitTarget) this.submitTarget.disabled = true
  }

  firstClientSideProblem() {
    if (this.hasCuratorIdTarget && !this.curatorIdTarget.value) return "Select a curator before saving."

    const lines = this.activeLines()
    const today = new Date().toISOString().slice(0, 10)
    // A line is usable if it identifies a release, OR is an existing placement line
    // (has a saved id or a known client). Imported lines carry a client but no
    // release, and they must remain editable and saveable. A playlist is always
    // optional (no-playlist is the legal awaiting_playlist state).
    const hasRelease = (l) => !!(
      (l.querySelector(".line-release-id")?.value) ||
      (l.querySelector(".line-nr-song")?.value) ||
      (l.querySelector("[data-line-hidden='id']")?.value) ||
      (l.querySelector(".line-client-id")?.value)
    )

    if (lines.length === 0 || !lines.some(l => hasRelease(l))) {
      return this.isPitched()
        ? "Add at least one release to pitch."
        : "At least one release is required to record a placement."
    }

    // A pitch needs only a curator and a release: no amount, playlist, or status.
    if (this.isPitched()) return null

    // Accepted payment fields (no longer HTML-required, so they can stay hidden in
    // Pitched without blocking submit; enforced here and on the server instead).
    const payDate = this.element.querySelector("[name='payment[payment_date]']")?.value
    if (!payDate) return "Enter a payment date."
    if (!this.isZeroType() && !(parseFloat(this.hasTotalAmountTarget ? this.totalAmountTarget.value : "") > 0)) {
      return "Enter a total amount greater than 0 (use Free placement or Playlist trade for $0)."
    }

    let problem = null
    lines.forEach((line, i) => {
      if (problem) return
      const n = i + 1
      if (!hasRelease(line)) { problem = `Playlist placement ${n}: select a release or complete the new release fields.`; return }
      const es = line.querySelector(".line-entry-status")
      if (es && es.value === "expired") {
        const placed = line.querySelector(".line-placed-date")?.value
        const end = line.querySelector(".line-end-date")?.value
        if (end && end > today) { problem = `Playlist placement ${n}: end date cannot be in the future.`; return }
        if (placed && end && placed > end) { problem = `Playlist placement ${n}: placed date cannot be after the end date.`; return }
      }
    })
    return problem
  }

  isImbalanced() {
    if (!this.isSplitOn() || this.isZeroType()) return false
    const total = parseFloat(this.totalAmountTarget.value) || 0
    const allocated = this.lineAmountInputs().reduce((s, i) => s + (parseFloat(i.value) || 0), 0)
    return Math.abs(Math.round((allocated - total) * 100) / 100) > 0.005
  }

  flashMessage(message) {
    if (!this.hasFormMessageTarget) return
    const el = this.formMessageTarget
    el.textContent = message
    el.style.display = "block"
    el.scrollIntoView({ behavior: "smooth", block: "center" })
    el.classList.remove("spt-allocation-flash")
    void el.offsetWidth
    el.classList.add("spt-allocation-flash")
    // Auto-dismiss after 5 seconds so the message does not linger and confuse.
    if (this.flashTimer) clearTimeout(this.flashTimer)
    this.flashTimer = setTimeout(() => {
      el.style.display = "none"
      el.textContent = ""
      el.classList.remove("spt-allocation-flash")
    }, 5000)
  }

  flashAllocation() {
    if (!this.hasAllocationTarget) return
    const el = this.allocationTarget
    el.scrollIntoView({ behavior: "smooth", block: "center" })
    el.classList.remove("spt-allocation-flash")
    void el.offsetWidth
    el.classList.add("spt-allocation-flash")
    setTimeout(() => el.classList.remove("spt-allocation-flash"), 1200)
  }

  // Safe in text AND in a quoted attribute: a curator controls playlist
  // names, and a name with a quote must never break out of data-...="".
  esc(text) {
    if (text === null || text === undefined) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
